import reading from './raamGardensReadings.json';
import type { BuildContext } from '../../types';
import type { PolygonIndex } from '../../../geo/polygonIndex';
import type { Vec2 } from '../../../geo/types';
import { intersection } from '../../../geo/clip';
import { polygonArea } from '../../../geo/polygon';
import { footprintInside, lotsOverlap, obliqueCorners } from '../lotFootprint';
import { obbFromFrontEdge } from '../lotObb';
import { measureLine } from '../measuredLine';
import type { Lot, ReservedFootprint } from '../types';
import { planRaamPerimeter } from './raamPerimeterPlan';

/** Fragmentary foundations constrain depth; the missing plan and upper body stay inferred. */
export function planRaamGardens(
  ctx: Pick<BuildContext, 'layout' | 'site'>,
  buildable: PolygonIndex,
  reserved: readonly ReservedFootprint[]
) {
  const source = ctx.layout.streets.find((s) => s.id === reading.source);
  if (!source || !ctx.layout.gardens.some((g) => g.id === 'garden-raamsteeg-1'))
    return { lots: [], reserved: [], unresolved: [] };
  const line = measureLine(
      source.centerline,
      source.centerline.map(() => source.width)
    ),
    lots: Lot[] = [],
    unresolved: string[] = [];
  for (const [i, house] of reading.houses.entries()) {
    const { point, tangent } = line.pointAt(house.chainage),
      inward: Vec2 = [tangent[1], -tangent[0]],
      facing: Vec2 = [-inward[0], -inward[1]],
      front: Vec2 = [
        point[0] + inward[0] * (source.width / 2 + 0.05),
        point[1] + inward[1] * (source.width / 2 + 0.05),
      ],
      corners = obliqueCorners(front, facing, house.width, house.depth, 0),
      lot: Lot = {
        index: lots.length,
        sourceId: source.id,
        side: 'right',
        ordinal: 54_000 + i,
        kind: 'house',
        facing,
        frontage: house.width,
        depth: house.depth,
        corners,
        obb: obbFromFrontEdge(corners[0], corners[1], house.depth, facing),
        plot: { frontMid: front, shear: 0 },
        reconstruction: {
          id: house.id,
          row: 'raam-archaeological-buildings',
          evidence: 'inferred',
          params: {
            storeys: 2,
            storeyHeight: 2.8,
            pitch: 0.85,
            frontType: 'brick',
            frontColour: 'brick',
            roofMaterial: 'tile',
            frontGable: 'plain',
            backGable: 'plain',
            chimneys: [],
          },
        },
      };
    if (
      house.chainage > line.length ||
      !footprintInside(buildable, corners) ||
      lots.some((other) => lotsOverlap(lot, other, 0.001)) ||
      reserved.some((r) =>
        intersection([{ outer: [...corners], holes: [] }], [r.polygon]).some(
          (p) => polygonArea(p) > 0.001
        )
      )
    ) {
      unresolved.push(house.id);
      continue;
    }
    lots.push(lot);
  }
  const footprints = lots.map((lot): ReservedFootprint => ({
      id: `surveyed:${lot.reconstruction!.id}`,
      polygon: { outer: [...lot.corners], holes: [] },
    })),
    perimeter = planRaamPerimeter(ctx, buildable, [...reserved, ...footprints]);
  return {
    lots: [...lots, ...perimeter.lots],
    reserved: [...footprints, ...perimeter.reserved],
    unresolved: [...unresolved, ...perimeter.unresolved],
  };
}
